import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminPageWrapper } from '@/components/admin/AdminPageWrapper';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Package, 
  Truck, 
  User,
  MapPin,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Save,
  ExternalLink,
  Image,
  AlertTriangle,
} from 'lucide-react';
import { NotificationBell } from '@/components/NotificationBell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status options
const STATUS_OPTIONS = [
  { value: "pending", label: "En attente" },
  { value: "processing", label: "En traitement" },
  { value: "sent_to_agent", label: "Envoyé à l'agent" },
  { value: "at_warehouse", label: "En entrepôt" },
  { value: "qc_pending", label: "QC en attente" },
  { value: "qc_approved", label: "QC approuvé" },
  { value: "shipped", label: "Expédié" },
  { value: "in_transit", label: "En transit" },
  { value: "delivered", label: "Livré" },
];

const STATUS_COLORS = {
  pending: "bg-zinc-500",
  processing: "bg-blue-500",
  sent_to_agent: "bg-purple-500",
  at_warehouse: "bg-indigo-500",
  qc_pending: "bg-yellow-500",
  qc_approved: "bg-green-500",
  shipped: "bg-cyan-500",
  in_transit: "bg-teal-500",
  delivered: "bg-green-600",
  paid: "bg-green-500",
  cancelled: "bg-red-500",
};

export function AdminOrderDetailPage() {
  const { orderNumber } = useParams();
  const navigate = useNavigate();
  
  const [order, setOrder] = useState(null);
  const [subOrders, setSubOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const getToken = () => localStorage.getItem('admin_token');

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      
      if (!token) {
        navigate('/admin');
        return;
      }

      // Fetch order details
      const orderResponse = await fetch(`${API_URL}/api/admin/orders/${orderNumber}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!orderResponse.ok) {
        if (orderResponse.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
          return;
        }
        throw new Error('Failed to fetch order');
      }

      const orderData = await orderResponse.json();
      setOrder(orderData);

      // Fetch sub-orders if multi-agent
      if (orderData.is_multi_agent) {
        const subOrdersResponse = await fetch(`${API_URL}/api/sub-orders/order/${orderNumber}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (subOrdersResponse.ok) {
          const subOrdersData = await subOrdersResponse.json();
          setSubOrders(subOrdersData.sub_orders || []);
          
          // Set first sub-order as active tab
          if (subOrdersData.sub_orders?.length > 0) {
            setActiveTab(`suborder-${subOrdersData.sub_orders[0].id}`);
          }
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('Erreur lors du chargement de la commande');
    } finally {
      setLoading(false);
    }
  }, [orderNumber, navigate]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const updateSubOrder = async (subOrderId, data) => {
    try {
      setSaving(true);
      const token = getToken();

      const response = await fetch(`${API_URL}/api/sub-orders/${subOrderId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        toast.success('Sous-commande mise à jour');
        fetchOrder();
      } else {
        throw new Error('Update failed');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const approveQC = async (subOrderId) => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/sub-orders/${subOrderId}/qc/approve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('QC approuvé');
        fetchOrder();
      }
    } catch (error) {
      toast.error('Erreur lors de l\'approbation QC');
    }
  };

  const formatPrice = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR');
  };

  const getStatusBadge = (status) => {
    const color = STATUS_COLORS[status] || STATUS_COLORS.pending;
    const label = STATUS_OPTIONS.find(s => s.value === status)?.label || status;
    return <Badge className={`${color} text-white`}>{label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!order) {
    return (
      <AdminPageWrapper title="Commande non trouvée" subtitle="">
        <p className="text-center py-20">Commande non trouvée</p>
      </AdminPageWrapper>
    );
  }

  const isMultiAgent = order.is_multi_agent && subOrders.length > 0;

  return (
    <AdminPageWrapper 
      title={`Commande #${order.order_number}`} 
      subtitle={`Créée le ${formatDate(order.created_at)}`}
    >
      <div className="space-y-6" data-testid="admin-order-detail-page">
        {isMultiAgent ? (
          /* Multi-Agent View with Tabs */
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-zinc-900 border border-zinc-800">
              <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-800">
                Vue d'ensemble
              </TabsTrigger>
              {subOrders.map((so, idx) => (
                <TabsTrigger 
                  key={so.id} 
                  value={`suborder-${so.id}`}
                  className="data-[state=active]:bg-zinc-800"
                  data-testid={`tab-suborder-${idx + 1}`}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Colis {idx + 1} ({so.agent_code})
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Order Info */}
                <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white">Informations commande</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white">Client</Label>
                        <p className="text-white">{order.customer?.email || '-'}</p>
                      </div>
                      <div>
                        <Label className="text-white">Total</Label>
                        <p className="text-white font-bold">
                          {formatPrice(order.pricing?.total, order.pricing?.currency)}
                        </p>
                      </div>
                    </div>

                    <Separator className="bg-zinc-800" />

                    <div>
                      <Label className="text-white mb-2 block">Sous-commandes</Label>
                      <div className="space-y-2">
                        {subOrders.map((so, idx) => (
                          <div 
                            key={so.id}
                            className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                                <Package className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="text-white font-medium">
                                  Colis {idx + 1} — {so.agent_name}
                                </p>
                                <p className="text-sm text-white">
                                  {so.items?.length || 0} article(s)
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {getStatusBadge(so.status)}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setActiveTab(`suborder-${so.id}`)}
                              >
                                Voir
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Customer & Shipping */}
                <Card className="bg-zinc-900/50 border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Livraison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-zinc-300">
                      <p className="font-medium">
                        {order.shipping?.address?.first_name} {order.shipping?.address?.last_name}
                      </p>
                      <p>{order.shipping?.address?.address_line1}</p>
                      {order.shipping?.address?.address_line2 && (
                        <p>{order.shipping?.address?.address_line2}</p>
                      )}
                      <p>
                        {order.shipping?.address?.postal_code} {order.shipping?.address?.city}
                      </p>
                      <p>{order.shipping?.address?.country}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Sub-Order Tabs */}
            {subOrders.map((so, idx) => (
              <TabsContent key={so.id} value={`suborder-${so.id}`}>
                <SubOrderPanel 
                  subOrder={so}
                  packageNumber={idx + 1}
                  orderItems={order.items}
                  onUpdate={(data) => updateSubOrder(so.id, data)}
                  onApproveQC={() => approveQC(so.id)}
                  saving={saving}
                />
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          /* Single Agent View */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white">Articles</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {order.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-3 bg-zinc-800/30 rounded-lg">
                      <div className="w-16 h-16 bg-zinc-800 rounded-lg overflow-hidden">
                        {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.name?.fr || item.name}</p>
                        <p className="text-sm text-white">
                          {item.variant?.attributes?.size && `Taille ${item.variant.attributes.size}`}
                          {item.variant?.attributes?.color && ` • ${item.variant.attributes.color}`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-white">{formatPrice(item.price, order.pricing?.currency)}</p>
                        <p className="text-sm text-white">Qté: {item.quantity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-white">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between text-white">
                  <span>Sous-total</span>
                  <span>{formatPrice(order.pricing?.subtotal, order.pricing?.currency)}</span>
                </div>
                <div className="flex justify-between text-white">
                  <span>Livraison</span>
                  <span>{formatPrice(order.pricing?.shipping, order.pricing?.currency)}</span>
                </div>
                <Separator className="bg-zinc-800" />
                <div className="flex justify-between text-white font-bold">
                  <span>Total</span>
                  <span>{formatPrice(order.pricing?.total, order.pricing?.currency)}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminPageWrapper>
  );
}

// Sub-Order Panel Component
function SubOrderPanel({ subOrder, packageNumber, orderItems, onUpdate, onApproveQC, saving }) {
  const [status, setStatus] = useState(subOrder.status);
  const [trackingNumber, setTrackingNumber] = useState(subOrder.tracking_number || '');
  const [agentOrderNumber, setAgentOrderNumber] = useState(subOrder.agent_order_number || '');

  const handleSave = () => {
    onUpdate({
      status,
      tracking_number: trackingNumber || null,
      agent_order_number: agentOrderNumber || null,
    });
  };

  // Get items for this sub-order
  const items = subOrder.items || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Main Info */}
      <Card className="bg-zinc-900/50 border-zinc-800 lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Package className="w-5 h-5" />
            Colis {packageNumber} — {subOrder.agent_name}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status & Tracking */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white mb-2 block">Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white mb-2 block">N° commande agent</Label>
              <Input
                value={agentOrderNumber}
                onChange={(e) => setAgentOrderNumber(e.target.value)}
                placeholder="Ex: ACB-123456"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>

          <div>
            <Label className="text-white mb-2 block">N° de suivi</Label>
            <div className="flex gap-2">
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Ex: YT123456789CN"
                className="bg-zinc-800 border-zinc-700 flex-1"
              />
              {trackingNumber && (
                <Button
                  variant="outline"
                  className="border-zinc-700"
                  onClick={() => window.open(`https://t.17track.net/en#nums=${trackingNumber}`, '_blank')}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full"
            data-testid={`save-suborder-${packageNumber}`}
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>

          <Separator className="bg-zinc-800" />

          {/* Items */}
          <div>
            <Label className="text-white mb-2 block">Articles ({items.length})</Label>
            <div className="space-y-2">
              {items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-zinc-800/30 rounded-lg">
                  <div className="w-12 h-12 bg-zinc-800 rounded overflow-hidden">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-white text-sm">{item.name?.fr || item.name}</p>
                    <p className="text-xs text-white">
                      {item.variant?.attributes?.size && `Taille ${item.variant.attributes.size}`}
                    </p>
                  </div>
                  <span className="text-white text-sm">×{item.quantity}</span>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QC Panel */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Image className="w-5 h-5" />
            Contrôle Qualité
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {subOrder.qc_status === 'approved' ? (
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span>QC Approuvé</span>
            </div>
          ) : subOrder.qc_status === 'rejected' ? (
            <div className="flex items-center gap-2 text-red-400">
              <XCircle className="w-5 h-5" />
              <span>QC Rejeté</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-yellow-400">
              <Clock className="w-5 h-5" />
              <span>En attente de QC</span>
            </div>
          )}

          {subOrder.qc_photos?.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {subOrder.qc_photos.map((photo, idx) => (
                <div 
                  key={idx}
                  className="aspect-square bg-zinc-800 rounded-lg overflow-hidden cursor-pointer"
                  onClick={() => window.open(photo, '_blank')}
                >
                  <img src={photo} alt={`QC ${idx + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white text-sm">Aucune photo QC disponible</p>
          )}

          {subOrder.qc_status === 'pending' && subOrder.qc_photos?.length > 0 && (
            <div className="flex gap-2">
              <Button 
                onClick={onApproveQC}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approuver
              </Button>
              <Button 
                variant="outline"
                className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Rejeter
              </Button>
            </div>
          )}

          {/* Actions */}
          <Separator className="bg-zinc-800" />
          
          <div className="space-y-2">
            <Button variant="outline" className="w-full border-zinc-700" size="sm">
              <RefreshCw className="w-4 h-4 mr-2" />
              Relancer Playwright
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminOrderDetailPage;
